import { useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CurrentUser } from '../../domain/entities/User';
import { MineMembership } from '../../domain/value-objects/MineMembership';
import { OrganizationMembership } from '../../domain/value-objects/OrganizationMembership';
import {
  UserRole,
  isMineClientRole,
  isOrganizationRole,
  primaryUserRole,
} from '../../domain/value-objects/UserRole';
import { readStoredMineMemberships } from '../../infrastructure/auth/mineMembershipClaims';
import { readStoredOrganizationMemberships } from '../../infrastructure/auth/organizationMembershipClaims';
import { AcceptInviteCommand } from '../dto/auth/AcceptInviteCommand';
import { ChangePasswordCommand } from '../dto/auth/ChangePasswordCommand';
import { ForgotPasswordCommand } from '../dto/auth/ForgotPasswordCommand';
import { LoginCommand } from '../dto/auth/LoginCommand';
import { LoginResult } from '../dto/auth/LoginResult';
import { ResetPasswordCommand } from '../dto/auth/ResetPasswordCommand';
import { AuthService } from '../../infrastructure/auth/AuthService';
import { apiClient } from '../../infrastructure/api/ApiClient';
import { notificationService } from '../../infrastructure/notifications';

/**
 * authStore — MineMiner SPA auth state.
 *
 * sprint-104 (epic-013 Sprint B, step 2d / S5). Forked from `Frontend/`'s
 * authStore and slimmed for the carved MineMiner.API host:
 *
 *   - Single login action (`POST /api/mineminer/auth/login`).
 *   - sprint-130 S4 — sliding session: `extendSession` now performs a real token
 *     refresh against the carved `/api/mineminer/auth/refresh` endpoint, and a
 *     refresh triggered anywhere (the ApiClient 401-retry, the proactive monitor,
 *     or this action) syncs `tokenExpiresAt` back here via a registered callback.
 *   - The selectors that MineMiner components rely on (`useIsMineMinerRep`,
 *     `useIsMineMinerAdmin`, etc.) are preserved.
 */

/**
 * sprint-107 S1.5 — surface state for the forgot-password / reset-password
 * flows. Mirrors core MentionedMe's authStore so the page components can
 * gate their UI on a single `passwordResetState` instead of plumbing local
 * booleans.
 */
export type PasswordResetState = 'idle' | 'requesting' | 'emailSent' | 'resetting' | 'resetSuccess' | 'error';

/**
 * sprint-108 S1 — surface state for the accept-invite flow on the public
 * AcceptInvitePage. Distinct from PasswordResetState so the two pages don't
 * stomp on each other if a user has both forms open in adjacent tabs.
 */
export type AcceptInviteState = 'idle' | 'accepting' | 'acceptSuccess' | 'error';

/**
 * sprint-108 S2 — surface state for the signed-in change-password page.
 * Distinct from PasswordResetState / AcceptInviteState so an admin who has
 * the invite form open while also rotating their own password doesn't see
 * state thrash between the two surfaces.
 */
export type PasswordChangeState = 'idle' | 'changing' | 'changeSuccess' | 'error';

interface AuthState {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loadingMessage: string | null;
  error: string | null;

  tokenExpiresAt: string | null;

  sessionWarningShown: boolean;

  // sprint-107 S1.5 password-reset surface
  passwordResetState: PasswordResetState;
  passwordResetError: string | null;

  // sprint-108 S1 accept-invite surface
  acceptInviteState: AcceptInviteState;
  acceptInviteError: string | null;

  // sprint-108 S2 change-password surface
  passwordChangeState: PasswordChangeState;
  passwordChangeError: string | null;

  login: (command: LoginCommand) => Promise<void>;
  logout: () => Promise<void>;
  loadCurrentUser: () => Promise<void>;
  clearError: () => void;

  // sprint-107 S1.5
  forgotPassword: (command: ForgotPasswordCommand) => Promise<void>;
  resetPassword: (command: ResetPasswordCommand) => Promise<void>;
  resetPasswordResetState: () => void;

  // sprint-108 S1
  acceptInvite: (command: AcceptInviteCommand) => Promise<void>;
  resetAcceptInviteState: () => void;

  // sprint-108 S2
  changePassword: (command: ChangePasswordCommand) => Promise<void>;
  resetPasswordChangeState: () => void;

  showSessionWarning: () => void;
  hideSessionWarning: () => void;
  extendSession: () => Promise<void>;
}

const authService = new AuthService(apiClient);

function mapResultToUser(result: LoginResult): CurrentUser {
  const firstName = result.firstName ?? null;
  const lastName = result.lastName ?? null;
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const displayName = fullName.length > 0 ? fullName : result.email;
  const roles = result.roles ?? [];

  // sprint-217 (epic-036 Phase 1 increment 1D) — this line used to read
  //   parseUserRole(result.roles?.[0] ?? null) ?? 'MineMinerRep'
  // and it had two faults that only surfaced once client roles existed.
  //
  // 🔴 The fallback promoted client users to internal staff. `parseUserRole` returned null
  // for MineClientAdmin (the union had no client roles), so the `?? 'MineMinerRep'` fired
  // and every client user was stored as a MineMiner rep — which made `useIsMineMinerRep`
  // true and had `MineMinerRoute` admit them to the staff CRM. The API would still refuse
  // their data, so it was never a data breach, but the guard was reporting the opposite of
  // the truth. There is no safe staff default for an unrecognised role: the answer is null.
  //
  // ⚠️ `roles[0]` also picked arbitrarily for anyone holding more than one role, because the
  // array is unordered. `primaryUserRole` applies an explicit precedence instead.
  const role = primaryUserRole(roles);

  return {
    id: result.userId,
    email: result.email,
    userName: result.userName,
    firstName,
    lastName,
    displayName,
    role,
    roles,
    businessId: null,
    emailConfirmed: true,
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      loadingMessage: null,
      error: null,
      tokenExpiresAt: null,
      sessionWarningShown: false,
      passwordResetState: 'idle',
      passwordResetError: null,
      acceptInviteState: 'idle',
      acceptInviteError: null,
      passwordChangeState: 'idle',
      passwordChangeError: null,

      login: async (command: LoginCommand): Promise<void> => {
        set({ isLoading: true, loadingMessage: 'Signing in...', error: null });
        try {
          const result = await authService.login(command);

          if (!result.succeeded) {
            const errorMsg = result.errorMessage ?? 'Login failed';
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              loadingMessage: null,
              error: errorMsg,
              tokenExpiresAt: null,
            });
            throw new Error(errorMsg);
          }

          const user = mapResultToUser(result);

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            loadingMessage: null,
            error: null,
            tokenExpiresAt: result.expiresAt,
          });

          const greeting = user.firstName ?? user.displayName ?? user.email;
          notificationService.showSuccess(
            `Welcome back, ${greeting}!`,
            'Signed in',
          );
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed';
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            loadingMessage: null,
            error: errorMessage,
          });
          throw error;
        }
      },

      logout: async (): Promise<void> => {
        set({ isLoading: true, loadingMessage: 'Signing out...' });

        try {
          await authService.logout();
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            loadingMessage: null,
            error: null,
            tokenExpiresAt: null,
            sessionWarningShown: false,
          });

          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
      },

      loadCurrentUser: (): Promise<void> => {
        // The carved MineMiner host has no `/verify` endpoint. Trust the
        // persisted state: if a token is in localStorage and the auth-storage
        // record says the user is authenticated, leave them signed in. The
        // first authenticated request that fails with 401 will redirect to
        // /login (ApiClient interceptor). Returning a resolved Promise keeps
        // the existing async caller contract.
        if (!authService.isAuthenticated()) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            loadingMessage: null,
          });
        } else {
          set({ isLoading: false, loadingMessage: null, error: null });
        }
        return Promise.resolve();
      },

      clearError: (): void => {
        set({ error: null });
      },

      // sprint-107 S1.5 — request a password-reset email. Carved host always
      // returns 200 (email-enumeration protection) so the only failure mode
      // here is a transport error.
      forgotPassword: async (command: ForgotPasswordCommand): Promise<void> => {
        set({ passwordResetState: 'requesting', passwordResetError: null });
        try {
          await authService.forgotPassword(command);
          set({ passwordResetState: 'emailSent', passwordResetError: null });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to request password reset.';
          set({ passwordResetState: 'error', passwordResetError: message });
          throw err;
        }
      },

      // sprint-107 S1.5 — complete a password reset. 400 from the API
      // surfaces via ApiClient/ApiError; we capture the message and let the
      // page render it.
      resetPassword: async (command: ResetPasswordCommand): Promise<void> => {
        set({ passwordResetState: 'resetting', passwordResetError: null });
        try {
          const result = await authService.resetPassword(command);
          if (!result.succeeded) {
            const message = result.error
              ?? result.errors?.join(' ')
              ?? 'Password reset failed.';
            set({ passwordResetState: 'error', passwordResetError: message });
            throw new Error(message);
          }
          set({ passwordResetState: 'resetSuccess', passwordResetError: null });
          notificationService.showSuccess(
            'Your password has been reset. Sign in with your new password.',
            'Password reset',
          );
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Password reset failed.';
          set({ passwordResetState: 'error', passwordResetError: message });
          throw err;
        }
      },

      resetPasswordResetState: (): void => {
        set({ passwordResetState: 'idle', passwordResetError: null });
      },

      // sprint-108 S1 — anonymous accept-invite. Mirrors resetPassword shape:
      // 400 from the API surfaces via ApiClient/ApiError and we let the page
      // render the message. Bot guards (honeypot + form-load-timestamp) are
      // carried in the command payload.
      acceptInvite: async (command: AcceptInviteCommand): Promise<void> => {
        set({ acceptInviteState: 'accepting', acceptInviteError: null });
        try {
          const result = await authService.acceptInvite(command);
          if (!result.succeeded) {
            const message = result.error
              ?? result.errors?.join(' ')
              ?? 'Invite could not be accepted.';
            set({ acceptInviteState: 'error', acceptInviteError: message });
            throw new Error(message);
          }
          set({ acceptInviteState: 'acceptSuccess', acceptInviteError: null });
          notificationService.showSuccess(
            'Your account is set up. Sign in with your new password.',
            'Invite accepted',
          );
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Invite could not be accepted.';
          set({ acceptInviteState: 'error', acceptInviteError: message });
          throw err;
        }
      },

      resetAcceptInviteState: (): void => {
        set({ acceptInviteState: 'idle', acceptInviteError: null });
      },

      // sprint-108 S2 — auth-gated change-password. Mirrors resetPassword
      // shape; 400 from the API surfaces via ApiClient/ApiError and we let
      // the page render the message (wrong current vs policy violation).
      changePassword: async (command: ChangePasswordCommand): Promise<void> => {
        set({ passwordChangeState: 'changing', passwordChangeError: null });
        try {
          const result = await authService.changePassword(command);
          if (!result.succeeded) {
            const message = result.error
              ?? result.errors?.join(' ')
              ?? 'Password change failed.';
            set({ passwordChangeState: 'error', passwordChangeError: message });
            throw new Error(message);
          }
          set({ passwordChangeState: 'changeSuccess', passwordChangeError: null });
          notificationService.showSuccess(
            'Your password has been changed. Sign in again with your new password.',
            'Password changed',
          );
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Password change failed.';
          set({ passwordChangeState: 'error', passwordChangeError: message });
          throw err;
        }
      },

      resetPasswordChangeState: (): void => {
        set({ passwordChangeState: 'idle', passwordChangeError: null });
      },

      showSessionWarning: (): void => {
        set({ sessionWarningShown: true });
      },

      hideSessionWarning: (): void => {
        set({ sessionWarningShown: false });
      },

      // sprint-130 S4 — slide the session by rotating the token pair. On success
      // the registered onTokensRefreshed callback updates tokenExpiresAt and we
      // dismiss the warning; on failure we leave the warning up (the next API
      // call's 401 path, or an expired refresh token, routes the rep to /login).
      extendSession: async (): Promise<void> => {
        const refreshed = await authService.refresh();
        if (refreshed) {
          set({ sessionWarningShown: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        tokenExpiresAt: state.tokenExpiresAt,
      }),
    },
  ),
);

// sprint-130 S4 — keep tokenExpiresAt in sync whenever the ApiClient (or the
// proactive monitor) silently rotates the token, so the session-timeout warning
// always counts down against the live expiry. Registered once at module load.
apiClient.registerTokensRefreshedCallback((expiresAt: string): void => {
  useAuthStore.setState({ tokenExpiresAt: expiresAt });
});

/**
 * Get current user's role
 */
export const useUserRole = (): UserRole | null => {
  return useAuthStore((state) => state.user?.role ?? null);
};

/**
 * Check if user is the platform-wide admin (superset of MineMiner admin).
 */
export const useIsAdmin = (): boolean => {
  return useAuthStore((state) => state.user?.role === 'Admin');
};

/**
 * Check if user is a MineMiner rep.
 * Satisfied by MineMinerRep, MineMinerAdmin, or the platform-wide Admin role
 * (matches the backend `RequireMineMinerRepRole` policy — Admin is treated as
 * a superset for support/debugging access).
 */
export const useIsMineMinerRep = (): boolean => {
  return useAuthStore((state) => {
    const role = state.user?.role;
    return role === 'MineMinerRep' || role === 'MineMinerAdmin' || role === 'Admin';
  });
};

/**
 * Check if user is a MineMiner admin.
 * Satisfied by MineMinerAdmin or the platform-wide Admin role.
 */
export const useIsMineMinerAdmin = (): boolean => {
  return useAuthStore((state) => {
    const role = state.user?.role;
    return role === 'MineMinerAdmin' || role === 'Admin';
  });
};

/**
 * Check if the user holds any of the three mine-scoped client roles.
 *
 * sprint-217 (epic-036 Phase 1 increment 1D). 🔴 Deliberately NOT satisfied by the staff
 * roles, and in particular not by the platform-wide `Admin`. The staff selectors above treat
 * Admin as a superset for support access, which is right for an internal tool; the client
 * area is a customer's own data and there is no support reason to render it as though a
 * customer were looking at it. An internal admin who genuinely needs to see a client's view
 * gets a client role and a membership like anyone else.
 */
export const useIsMineClient = (): boolean => {
  return useAuthStore((state) => (state.user?.roles ?? []).some(isMineClientRole));
};

/**
 * Check if the user is a client mine back-office admin at ANY of their mines.
 *
 * 🔴 "At any mine" is the whole caveat. This answers "should the SPA draw admin-only
 * controls at all", never "may this person act on THIS mine" — that is a server decision,
 * per mine, against a live MineMembership row.
 */
export const useIsMineClientAdmin = (): boolean => {
  return useAuthStore((state) => (state.user?.roles ?? []).includes('MineClientAdmin'));
};

/**
 * Check if the user holds the Legal read persona at ANY of their mines.
 *
 * sprint-252 (epic-036 Phase 4 increment 4H, DR-041). The same caveat as the Admin selector
 * above: this answers "should the SPA draw the portfolio surface at all", never "which mines
 * may this person read" — the portfolio itself is resolved by the SERVER from live
 * MineMembership rows on every request, and an empty or stale answer here costs a redirect,
 * not data.
 */
export const useIsMineClientLegal = (): boolean => {
  return useAuthStore((state) => (state.user?.roles ?? []).includes('MineClientLegal'));
};

/**
 * The signed-in user's mine memberships, read from the access token's claims.
 *
 * 🔴 RENDERING ONLY — see `mineMembershipClaims.ts`. Subscribes to `isAuthenticated` so the
 * value is recomputed on sign-in and sign-out; the claims themselves live on the token in
 * localStorage rather than in this store, because the token is what the ApiClient rotates.
 */
export const useMineMemberships = (): MineMembership[] => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  // Memoised because the reader builds a fresh array every call: an unmemoised value would
  // be a new reference on every render and would loop any consumer that puts it in a
  // dependency array.
  return useMemo(() => (isAuthenticated ? readStoredMineMemberships() : []), [isAuthenticated]);
};

/**
 * Check if the user holds either of the two organization-scoped roles.
 *
 * sprint-239 (epic-039 Canary increment A). 🔴 Deliberately NOT satisfied by the staff roles,
 * not by the platform-wide `Admin`, and not by any mine-client role — the last is DR-002
 * clause 2 in selector form. An organization is a customer's own data; an internal admin who
 * genuinely needs to see it gets an organization role and a membership like anyone else.
 */
export const useIsOrganizationMember = (): boolean => {
  return useAuthStore((state) => (state.user?.roles ?? []).some(isOrganizationRole));
};

/**
 * Check if the user is an organization back-office admin at ANY of their organizations.
 *
 * 🔴 "At any organization" is the whole caveat. This answers "should the SPA draw admin-only
 * controls at all", never "may this person act on THIS organization" — that is a server
 * decision, per organization, against a live OrganizationMembership row.
 */
export const useIsOrganizationAdmin = (): boolean => {
  return useAuthStore((state) => (state.user?.roles ?? []).includes('OrganizationAdmin'));
};

/**
 * The signed-in user's organization memberships, read from the access token's claims.
 *
 * 🔴 RENDERING ONLY — see `organizationMembershipClaims.ts`. Memoised on `isAuthenticated`
 * for the same reason `useMineMemberships` is: the reader builds a fresh array every call, and
 * an unmemoised value would be a new reference on every render.
 */
export const useOrganizationMemberships = (): OrganizationMembership[] => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return useMemo(
    () => (isAuthenticated ? readStoredOrganizationMemberships() : []),
    [isAuthenticated]
  );
};

export const useAuthLoading = (): boolean => {
  return useAuthStore((state) => state.isLoading);
};

export const useAuthError = (): string | null => {
  return useAuthStore((state) => state.error);
};

export const useTokenExpiresAt = (): string | null => {
  return useAuthStore((state) => state.tokenExpiresAt);
};

export const useSessionWarning = (): boolean => {
  return useAuthStore((state) => state.sessionWarningShown);
};
